import React, { ComponentProps, FC } from 'react'

import { useFragment } from '@apollo/client'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { AtsRequisitionApprovableFragmentDoc } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  EmployeeTableViewRow,
  useEmployeeTableViewRow,
  useTeamTableViewRow,
} from 'modules/inbox_tasks/activities/Task/utils/content'
import NoteField from 'modules/inbox_tasks/components/NoteField'

type AtsRequisitionTaskContentProps = {
  id: number
  requesterContent: EmployeeTableViewRow
}

const AtsRequisitionTaskContent: FC<AtsRequisitionTaskContentProps> = ({
  id,
  requesterContent,
}) => {
  const { data } = useFragment({
    fragment: AtsRequisitionApprovableFragmentDoc,
    fragmentName: 'AtsRequisitionApprovable',
    from: {
      __typename: 'AtsRequisition',
      id,
    },
    optimistic: false,
  })

  const processOwnerContent = useEmployeeTableViewRow({
    employee: data?.recruiter?.employeeName,
    title: i18n.t('dashboard.todo.approvals.ats.requisition_owner'),
  })

  const hiringManagerContent = useEmployeeTableViewRow({
    employee: data?.hiringManager?.employeeName,
    title: i18n.t('dashboard.todo.approvals.ats.hiring_manager'),
  })

  const teamContent = useTeamTableViewRow({
    team: data?.team,
    title: i18n.t('dashboard.todo.approvals.ats.team'),
  })

  if (!data) return null

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    requesterContent,
    data.jobPostingTitle && {
      title: i18n.t('dashboard.todo.approvals.ats.job_opening_title'),
      content: { text: data.jobPostingTitle, type: 'item' as const },
    },
    teamContent,
    data.workplace?.city && {
      title: i18n.t('dashboard.todo.approvals.ats.location'),
      content: { text: data.workplace.city, type: 'item' as const },
    },
    data.requisitionReason && {
      title: i18n.t('dashboard.todo.approvals.ats.reason'),
      content: { text: data.requisitionReason, type: 'item' as const },
    },
    hiringManagerContent,
    processOwnerContent,
  ].filter(Boolean)

  return (
    <>
      <DetailsItemsList details={details} tableView />
      {!!data.note && (
        <NoteField label={i18n.t('dashboard.todo.approvals.ats.note')} content={data.note} />
      )}
    </>
  )
}

export default AtsRequisitionTaskContent

export * from './metadata'
